import { EmptyProps } from "@/Common/Components/BaseReactComponent";
import { Note } from "@/Common/Models/Note";
import { DocumentUIState, InboundFaxesUIStateInbox } from "@/Common/Models/UI/Enums";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { AttachmentsTooltipControl } from "@/Documents/Components/Attachments/AttachmentsTooltipControl";
import { DocumentEdit } from "@/Documents/Components/DocumentEdit/DocumentEdit";
import { DocumentNotesPopup } from "@/Documents/Components/DocumentNotes/DocumentNotesPopup";
import { DocumentSearch } from "@/Documents/Components/DocumentSearch/DocumentSearch";
import { DocumentTreeControl } from "@/Documents/Components/DocumentTreeControl/DocumentTreeControl";
import { DocumentView } from "@/Documents/Components/DocumentView/DocumentView";
import { DocumentExtensionContentRegistryView } from "@/Documents/Extensions/DocumentExtensionContentRegistry";
import { InboundFaxMailboxModel } from "@/Documents/Models/InboundFax/InboundFaxMailboxModel";
import { IDocumentsTabModel } from "@/Documents/Models/UI/IDocumentsTabModel";
import { DocumentCreate } from "./DocumentCreate/DocumentCreate";
import { DocumentInAppView, DocumentNavigation } from "./DocumentInAppView/DocumentInAppView";
import { InboundFaxesView } from "./InboundFax/InboundFaxesView";

import styles from "./DocumentsTab.module.scss";

class DocumentsTabState {
    public Model: IDocumentsTabModel;
    public ColumnManagerModel: Immutable.List<ColumnConfigurationModel>;
    public InboundFaxInboxModel: InboundFaxMailboxModel;
    public InboundFaxTrashModel: InboundFaxMailboxModel;
    public InboundFaxIndexedModel: InboundFaxMailboxModel;
}

class DocumentsTab extends React.Component<EmptyProps, DocumentsTabState> {
    private changeListenerMethodId: string;

    constructor(props: EmptyProps) {
        super(props);

        // Restore and init state from cache
        documentsStore.ReturnStateToMainTab();
        documentsStore.InitializeForGlobalDocuments(false, true);
        const newState = new DocumentsTabState();
        newState.Model = documentsStore.DocumentsModel;
        newState.ColumnManagerModel = documentsStore.ColumnManagerModel;
        newState.InboundFaxInboxModel = documentsStore.InboundFaxInboxModel;
        newState.InboundFaxTrashModel = documentsStore.InboundFaxTrashModel;
        newState.InboundFaxIndexedModel = documentsStore.InboundFaxIndexedModel;
        this.state = newState;
    }

    public componentWillUnmount(): void {
        documentsStore.updateDocumentModelVisiblity(false);
        documentsStore.SaveState();
        documentsStore.RemoveChangeListener(this.changeListenerMethodId);
    }

    public render(): JSX.Element {
        var viewToReturn: JSX.Element = null;

        switch (this.state.Model.get("UIState")) {
            case DocumentUIState.Search:
                viewToReturn = (
                    <DocumentSearch
                        Model={this.state.Model}
                        ColumnManagerModel={this.state.ColumnManagerModel}
                    />
                );
                break;
            case DocumentUIState.Edit:
                viewToReturn = (
                    <DocumentEdit
                        model={this.state.Model.get("SelectedDocument")}
                        documentClasses={this.state.Model.get("DocumentClasses").toList()}
                        sources={this.state.Model.get("DocumentSources")}
                        descriptions={this.state.Model.get("DocumentDescription")}
                        beginUpload={this.state.Model.get("BeginUpload")}
                        sites={this.state.Model.get("Sites")}
                    />
                );
                break;
            case DocumentUIState.Create:
                viewToReturn = (
                    <DocumentCreate
                        model={this.state.Model.get("NewDocument")}
                        documentClasses={this.state.Model.get("DocumentClasses").toList()}
                        sources={this.state.Model.get("DocumentSources")}
                        docDescription={this.state.Model.get("DocumentDescription")}
                        beginUpload={this.state.Model.get("BeginUpload")}
                        sites={this.state.Model.get("Sites")}
                        isDocumentCreateFromPatient={false}
                    />
                );
                break;
            case DocumentUIState.View:
                viewToReturn = (
                    <DocumentView
                        model={this.state.Model.get("SelectedDocument")}
                        documentClasses={this.state.Model.get("DocumentClasses").toList()}
                        sources={this.state.Model.get("DocumentSources")}
                        descriptions={this.state.Model.get("DocumentDescription")}
                        documentTabModel={this.state.Model}
                    />
                );
                break;
            case DocumentUIState.Annotate:
                viewToReturn = (
                    <DocumentInAppView
                        document={this.state.Model.get("SelectedDocument")}
                        documentIndex={this.getDocumentIndex()}
                        documentCount={this.getDocumentCount()}
                        isSendToPortalPopupOpen={this.state.Model.get("IsSendToPatientPortalPopupOpen")}
                        onDocumentNavigation={navigation => this.onDocumentNavigation(navigation)}
                        portalLinkedItem={this.state.Model.get("PortalLinkedItemModel")}
                    />
                );
                break;
            case DocumentUIState.InboundFaxInboxMailbox:
                viewToReturn = (
                    <InboundFaxesView
                        inboxType={InboundFaxesUIStateInbox.Inbox}
                        mailboxModel={this.state.InboundFaxInboxModel}
                        descriptionsAutoCompleteList={this.state.Model.get("DocumentDescription")}
                        sourcesAutoCompleteList={this.state.Model.get("DocumentSources")}
                    />
                );
                break;
            case DocumentUIState.InboundFaxTrashMailbox:
                viewToReturn = (
                    <InboundFaxesView
                        inboxType={InboundFaxesUIStateInbox.Trash}
                        mailboxModel={this.state.InboundFaxTrashModel}
                    />
                );
                break;
            case DocumentUIState.InboundFaxIndexedMailbox:
                viewToReturn = (
                    <InboundFaxesView
                        inboxType={InboundFaxesUIStateInbox.Indexed}
                        mailboxModel={this.state.InboundFaxIndexedModel}
                    />
                );
                break;
            case DocumentUIState.Extension:
                viewToReturn = (
                    <div className={`workspace ${styles.extensionWorkspace}`}>
                        <div className={styles.extensionScrollableContent}>
                            <div className={styles.extensionAutoArea}>
                                <DocumentExtensionContentRegistryView selectedView={this.state.Model.get("SelectedView")} />
                            </div>
                        </div>
                    </div>
                );
                break;

            default:
                throw new TypeError("undefined view state");
        }
        const leftMenuStateCss = this.state.Model.get("LeftMenuOpen") ? "left-nav-menu documents responsive transition" : "left-nav-menu documents transition closed";
        const leftMenuControlsCss = this.state.Model.get("LeftMenuOpen") ? "left-nav-menu-handle responsive" : "left-nav-menu-handle";

        return (
            <div className="sub-container">
                {this.state.Model.get("UIState") !== DocumentUIState.Annotate && (
                    <>
                        <div className={leftMenuStateCss} data-cy="document-transition">
                            <div className="ws-scrollable hide-overflow">
                                <DocumentTreeControl
                                    DocumentClasses={this.state.Model.get("DocumentClasses").toList()}
                                    NonApprovedDocumentCount={this.state.Model.get("DocumentNonApprovedCount")}
                                    selectedView={this.state.Model.get("SelectedView")}
                                    isDeactivatedAccountEnabled={true}
                                    isUsedInGlobalDocumentTab={true}
                                />
                            </div>
                        </div>
                        <div className={leftMenuControlsCss}>
                            {this.state.Model.get("LeftMenuOpen") === true && (
                                <div onClick={() => this.collapseLeftMenu()} className="slide-menu-icon" data-cy="document-slide-menu-icon">
                                    <span />
                                    <span />
                                    <span />
                                </div>
                            )}
                            {this.state.Model.get("LeftMenuOpen") === false && (
                                <div onClick={() => this.expandLeftMenu()} className="slide-menu-icon" data-cy="document-slide-menu-icon">
                                    <span />
                                    <span />
                                    <span />
                                </div>
                            )}
                        </div>
                    </>
                )}

                {viewToReturn}

                {this.state.Model.get("NoteWindowOpen") &&
                    this.state.Model.get("SelectedDocument").get("MedicalDocumentInfo").Model && (
                        <DocumentNotesPopup
                            controlId={this.state.Model.get("NotesDropdownControlId")}
                            notes={this.state.Model.get("CurrentDocumentNotes").map(y => {
                                const note = new Note();
                                note.ContactId = y.ContactId;
                                note.NoteContent = y.NoteContent;
                                note.NoteDate = y.NoteDate;
                                note.NoteId = y.NoteId;

                                return note;
                            }).toArray()}
                            referenceId={this.state.Model.get("CurrentDocumentIDForNotes")}
                            eventToDispatchOnDropdownClosed={LocalOperations.Documents.Note_Dropdown_Closed}
                            model={this.state.Model.get("SelectedDocument")}
                        />
                )
                }

                {this.state.Model.get("MulitFilePopupOpen") === true && (
                    <AttachmentsTooltipControl
                        Attachements={this.state.Model.get("CurrentAttachements").toArray()}
                        leftPosition={this.state.Model.get("LeftPosition")}
                        topPosition={this.state.Model.get("TopPosition")}
                    />
                )}
            </div>
        );
    }

    public shouldComponentUpdate(_: EmptyProps, nextState: DocumentsTabState): boolean {
        return (
            this.state.Model !== nextState.Model
            || this.state.ColumnManagerModel !== nextState.ColumnManagerModel
            || this.state.InboundFaxInboxModel !== nextState.InboundFaxInboxModel
            || this.state.InboundFaxTrashModel !== nextState.InboundFaxTrashModel
            || this.state.InboundFaxIndexedModel !== nextState.InboundFaxIndexedModel
        );
    }

    public componentDidMount(): void {
        this.changeListenerMethodId = documentsStore.AddChangeListener(this.changeListener, this, "doc-tab-listener");

        if (this.state.Model.get("UIState") === DocumentUIState.Search)
            documentsStore.SearchDocuments();
    }

    private getDocumentIndex(): number {
        return this.state.Model.get("DocumentsTableData")
            .toArray()
            .findIndex(item => item.Model.Model.Id === this.state.Model.get("SelectedDocumentId"));
    }

    private getDocumentCount(): number {
        return documentsStore.DocumentsModel.get("DocumentsTableData").toArray().length;
    }

    public changeListener(): void {
        const newState = new DocumentsTabState();
        newState.Model = documentsStore.DocumentsModel;
        newState.ColumnManagerModel = documentsStore.ColumnManagerModel;
        newState.InboundFaxInboxModel = documentsStore.InboundFaxInboxModel;
        newState.InboundFaxTrashModel = documentsStore.InboundFaxTrashModel;
        newState.InboundFaxIndexedModel = documentsStore.InboundFaxIndexedModel;
        this.setState(newState);
    }

    private expandLeftMenu(): void {
        var op = new LocalEvent(LocalOperations.Documents.ExpandLeftMenu);
        LocalEventDispatcher.dispatch(op);
    }

    private collapseLeftMenu(): void {
        var op = new LocalEvent(LocalOperations.Documents.CollapseLeftMenu);
        LocalEventDispatcher.dispatch(op);
    }

    private onDocumentNavigation(navigation: DocumentNavigation): void {
        const documentList = this.state.Model.get("DocumentsTableData").toArray();
        const documentIndex = this.getDocumentIndex();
        const nextIndex = navigation === DocumentNavigation.Forward ? documentIndex + 1 : documentIndex - 1;

        if (nextIndex < 0 || nextIndex > documentList.length) {
            throw new Error("Navigation number is out of range for the document's list");
        }

        sessionStore.dispatch(LocalOperations.Documents.DisplayDocument, {
            docId: documentList[nextIndex].Model.Model.Id,
            annotationView: true
        });
    }
}

export { DocumentsTab };
